import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Link2,
  Unlink,
  RefreshCw,
  Save,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  Sparkles,
  Search,
  Calendar,
  DollarSign,
  Info,
  Check,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

export default function SiteMinderMappingTab({ API_URL, getToken, onRoomsUpdated }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isAutoSuggesting, setIsAutoSuggesting] = useState(false);
  const [savingRoomId, setSavingRoomId] = useState(null);

  // Core Data
  const [rooms, setRooms] = useState([]);
  const [siteminderTypes, setSiteminderTypes] = useState([]);
  const [siteminderRates, setSiteminderRates] = useState([]);
  const [statusInfo, setStatusInfo] = useState({
    connected: false,
    property_name: '',
    booking_engine_url: '',
    message: ''
  });

  // Current Working Mapping State: { [roomId]: { typeUuid, typeName, rateUuid, rateName, isManual } }
  const [mappings, setMappings] = useState({});
  const [initialMappings, setInitialMappings] = useState({});

  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'mapped' | 'unmapped'

  // Test Quote Modal
  const [testModal, setTestModal] = useState({
    open: false,
    room: null,
    checkIn: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    checkOut: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
    adults: 2,
    loading: false,
    result: null,
    error: null
  });

  const fetchMappingData = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/admin/siteminder/mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = res.data;
      setStatusInfo({
        connected: data.connected,
        property_name: data.property_name || '',
        booking_engine_url: data.booking_engine_url || '',
        message: data.message || ''
      });
      setSiteminderTypes(data.room_types || data.siteminder_room_types || []);
      setSiteminderRates(data.room_rates || data.siteminder_room_rates || []);

      const roomList = data.rooms || [];
      setRooms(roomList);

      const mapObj = {};
      roomList.forEach((r) => {
        mapObj[r.room_type_id] = {
          room_type_id: r.room_type_id,
          siteminder_room_type_uuid: r.siteminder_room_type_uuid || '',
          siteminder_room_type_name: r.siteminder_room_type_name || '',
          siteminder_room_rate_uuid: r.siteminder_room_rate_uuid || '',
          siteminder_room_rate_name: r.siteminder_room_rate_name || '',
          is_manual_mapping: Boolean(r.is_manual_mapping)
        };
      });
      setMappings(mapObj);
      setInitialMappings(JSON.parse(JSON.stringify(mapObj)));
    } catch (error) {
      console.error('Error fetching mapping data:', error);
      toast.error('Gagal mengambil data pemetaan SiteMinder');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMappingData();
  }, []);

  // Detect which rooms have unsaved changes
  const dirtyRoomIds = useMemo(() => {
    const dirty = new Set();
    Object.keys(mappings).forEach((id) => {
      const cur = mappings[id];
      const init = initialMappings[id] || {};
      if (
        cur.siteminder_room_type_uuid !== (init.siteminder_room_type_uuid || '') ||
        cur.siteminder_room_rate_uuid !== (init.siteminder_room_rate_uuid || '')
      ) {
        dirty.add(id);
      }
    });
    return dirty;
  }, [mappings, initialMappings]);

  // Handle changing Room Type in dropdown
  const handleTypeChange = (roomId, typeUuid) => {
    const selectedType = siteminderTypes.find((t) => t.uuid === typeUuid);
    const availableRatesForType = siteminderRates.filter((r) => r.roomTypeUuid === typeUuid);

    // If changing type, check if current rate belongs to this type
    const curRateUuid = mappings[roomId]?.siteminder_room_rate_uuid;
    const rateStillValid = availableRatesForType.some((r) => r.uuid === curRateUuid);

    let nextRateUuid = rateStillValid ? curRateUuid : '';
    let nextRateName = rateStillValid ? mappings[roomId]?.siteminder_room_rate_name : '';

    // If only 1 rate available for this type, auto-select it
    if (!nextRateUuid && availableRatesForType.length === 1) {
      nextRateUuid = availableRatesForType[0].uuid;
      nextRateName = availableRatesForType[0].name;
    }

    setMappings((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        siteminder_room_type_uuid: typeUuid,
        siteminder_room_type_name: selectedType?.name || '',
        siteminder_room_rate_uuid: nextRateUuid,
        siteminder_room_rate_name: nextRateName,
        is_manual_mapping: true
      }
    }));
  };

  // Handle changing Rate Plan in dropdown
  const handleRateChange = (roomId, rateUuid) => {
    if (!rateUuid) {
      // Clear rate
      setMappings((prev) => ({
        ...prev,
        [roomId]: {
          ...prev[roomId],
          siteminder_room_rate_uuid: '',
          siteminder_room_rate_name: '',
          is_manual_mapping: false
        }
      }));
      return;
    }

    const selectedRate = siteminderRates.find((r) => r.uuid === rateUuid);
    const parentTypeUuid = selectedRate?.roomTypeUuid || mappings[roomId]?.siteminder_room_type_uuid;
    const parentType = siteminderTypes.find((t) => t.uuid === parentTypeUuid);

    setMappings((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        siteminder_room_type_uuid: parentTypeUuid || '',
        siteminder_room_type_name: parentType?.name || prev[roomId]?.siteminder_room_type_name || '',
        siteminder_room_rate_uuid: rateUuid,
        siteminder_room_rate_name: selectedRate?.name || '',
        is_manual_mapping: true
      }
    }));
  };

  // Unlink a room
  const handleUnmapRoom = (roomId) => {
    setMappings((prev) => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        siteminder_room_type_uuid: '',
        siteminder_room_type_name: '',
        siteminder_room_rate_uuid: '',
        siteminder_room_rate_name: '',
        is_manual_mapping: false
      }
    }));
  };

  // Save single room mapping
  const handleSaveSingleRoom = async (roomId) => {
    const cur = mappings[roomId];
    if (!cur) return;
    setSavingRoomId(roomId);

    try {
      await axios.put(
        `${API_URL}/admin/siteminder/mappings/${roomId}`,
        {
          siteminder_room_type_uuid: cur.siteminder_room_type_uuid || null,
          siteminder_room_type_name: cur.siteminder_room_type_name || null,
          siteminder_room_rate_uuid: cur.siteminder_room_rate_uuid || null,
          siteminder_room_rate_name: cur.siteminder_room_rate_name || null,
          is_manual_mapping: Boolean(cur.siteminder_room_rate_uuid)
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      toast.success(`Pemetaan kamar berhasil disimpan`);
      setInitialMappings((prev) => ({
        ...prev,
        [roomId]: { ...cur }
      }));
      if (onRoomsUpdated) onRoomsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pemetaan kamar');
    } finally {
      setSavingRoomId(null);
    }
  };

  // Save all changed mappings
  const handleSaveAll = async () => {
    setIsSavingAll(true);
    const batchList = Object.values(mappings).map((m) => ({
      room_type_id: m.room_type_id,
      siteminder_room_type_uuid: m.siteminder_room_type_uuid || null,
      siteminder_room_type_name: m.siteminder_room_type_name || null,
      siteminder_room_rate_uuid: m.siteminder_room_rate_uuid || null,
      siteminder_room_rate_name: m.siteminder_room_rate_name || null,
      is_manual_mapping: Boolean(m.siteminder_room_rate_uuid)
    }));

    try {
      const res = await axios.post(
        `${API_URL}/admin/siteminder/mappings/batch`,
        { mappings: batchList },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      toast.success(res.data.message || 'Semua pemetaan berhasil disimpan');
      setInitialMappings(JSON.parse(JSON.stringify(mappings)));
      if (onRoomsUpdated) onRoomsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan perubahan');
    } finally {
      setIsSavingAll(false);
    }
  };

  // Auto-Suggest smart mappings
  const handleAutoSuggest = async () => {
    setIsAutoSuggesting(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/siteminder/mappings/auto-suggest`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      const suggestions = res.data.suggestions || [];
      let suggestedCount = 0;

      setMappings((prev) => {
        const next = { ...prev };
        suggestions.forEach((s) => {
          if (s.mapped && next[s.room_type_id]) {
            next[s.room_type_id] = {
              ...next[s.room_type_id],
              siteminder_room_type_uuid: s.siteminder_room_type_uuid || '',
              siteminder_room_type_name: s.siteminder_room_type_name || '',
              siteminder_room_rate_uuid: s.siteminder_room_rate_uuid || '',
              siteminder_room_rate_name: s.siteminder_room_rate_name || '',
              is_manual_mapping: true
            };
            suggestedCount++;
          }
        });
        return next;
      });

      toast.info(
        `${suggestedCount} rekomendasi pemetaan terpasang. Tinjau dan klik "Simpan Semua Pemetaan" untuk mengonfirmasi.`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghasilkan rekomendasi otomatis');
    } finally {
      setIsAutoSuggesting(false);
    }
  };

  // Test live quote for a room
  const openTestModal = (room) => {
    setTestModal({
      open: true,
      room,
      checkIn: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      checkOut: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
      adults: 2,
      loading: false,
      result: null,
      error: null
    });
  };

  const runTestQuote = async () => {
    if (!testModal.room) return;
    setTestModal((prev) => ({ ...prev, loading: true, result: null, error: null }));

    try {
      const res = await axios.post(
        `${API_URL}/admin/siteminder/test-quote`,
        {
          room_type_id: testModal.room.room_type_id,
          check_in: testModal.checkIn,
          check_out: testModal.checkOut,
          adults: parseInt(testModal.adults) || 2
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setTestModal((prev) => ({ ...prev, loading: false, result: res.data }));
    } catch (error) {
      setTestModal((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.detail || 'Gagal mengecek kuota ke SiteMinder'
      }));
    }
  };

  // Filtered rooms
  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      const matchSearch =
        room.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (mappings[room.room_type_id]?.siteminder_room_type_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (mappings[room.room_type_id]?.siteminder_room_rate_name || '').toLowerCase().includes(searchQuery.toLowerCase());

      const isMapped = Boolean(mappings[room.room_type_id]?.siteminder_room_rate_uuid);

      if (statusFilter === 'mapped' && !isMapped) return false;
      if (statusFilter === 'unmapped' && isMapped) return false;
      return matchSearch;
    });
  }, [rooms, searchQuery, statusFilter, mappings]);

  const mappedCount = useMemo(() => {
    return Object.values(mappings).filter((m) => Boolean(m.siteminder_room_rate_uuid)).length;
  }, [mappings]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-12 text-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Memuat data pemetaan kamar dan SiteMinder...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SiteMinder Header Banner */}
      <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-white border border-emerald-200 rounded-xl p-6 shadow-soft">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-600 text-white rounded-xl shadow-md mt-0.5">
              <Link2 className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-bold text-gray-900">
                  Pemetaan Kamar & Rate Plan SiteMinder
                </h3>
                {statusInfo.connected ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Terhubung ke Little Hotelier
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    {statusInfo.message || 'SiteMinder Belum Terhubung'}
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm mt-1 max-w-3xl">
                Sambungkan setiap tipe kamar di website Anda ke <strong>Tipe Kamar</strong> dan{' '}
                <strong>Rate Plan</strong> di SiteMinder. Sistem akan mengambil kuota allotment dan harga live secara
                otomatis berdasarkan rate plan yang dipilih.
              </p>
              {statusInfo.booking_engine_url && (
                <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
                  <span>Properti: <strong>{statusInfo.property_name}</strong></span>
                  <span>•</span>
                  <a
                    href={statusInfo.booking_engine_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 underline hover:text-emerald-900"
                  >
                    Buka Booking Engine Resmi <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-start lg:self-center">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMappingData}
              disabled={isLoading || isSyncing}
              className="bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
              Segarkan Data
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAutoSuggest}
              disabled={isAutoSuggesting || !statusInfo.connected}
              className="bg-white border-teal-300 text-teal-700 hover:bg-teal-50 text-xs"
              title="Cocokkan otomatis nama kamar website dengan rate plan SiteMinder"
            >
              <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${isAutoSuggesting ? 'animate-spin' : ''}`} />
              Auto-Suggest
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={isSavingAll || dirtyRoomIds.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs shadow-sm"
            >
              <Save className={`w-3.5 h-3.5 mr-1.5 ${isSavingAll ? 'animate-spin' : ''}`} />
              Simpan Semua ({dirtyRoomIds.size})
            </Button>
          </div>
        </div>

        {/* Stats Pill Bar */}
        <div className="mt-5 pt-4 border-t border-emerald-100 flex flex-wrap items-center gap-6 text-xs text-gray-600">
          <div>
            Total Kamar Web: <span className="font-bold text-gray-900">{rooms.length}</span>
          </div>
          <div>
            Terpetakan: <span className="font-bold text-emerald-700">{mappedCount}</span>
          </div>
          <div>
            Belum Terhubung: <span className="font-bold text-amber-700">{rooms.length - mappedCount}</span>
          </div>
          <div>
            Tipe Kamar SiteMinder: <span className="font-bold text-gray-900">{siteminderTypes.length}</span>
          </div>
          <div>
            Rate Plan SiteMinder: <span className="font-bold text-gray-900">{siteminderRates.length}</span>
          </div>
          {dirtyRoomIds.size > 0 && (
            <div className="ml-auto text-amber-800 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200 font-semibold flex items-center gap-1.5 animate-pulse">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
              {dirtyRoomIds.size} kamar belum disimpan
            </div>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-4 rounded-xl shadow-soft">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Cari kamar web atau SiteMinder..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs text-gray-500 whitespace-nowrap">Status:</span>
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 rounded-md font-medium transition-colors ${
                statusFilter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Semua ({rooms.length})
            </button>
            <button
              onClick={() => setStatusFilter('mapped')}
              className={`px-3 py-1 rounded-md font-medium transition-colors ${
                statusFilter === 'mapped' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Terhubung ({mappedCount})
            </button>
            <button
              onClick={() => setStatusFilter('unmapped')}
              className={`px-3 py-1 rounded-md font-medium transition-colors ${
                statusFilter === 'unmapped' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Belum ({rooms.length - mappedCount})
            </button>
          </div>
        </div>
      </div>

      {/* Mapping Cards / Table */}
      <div className="space-y-4">
        {filteredRooms.map((room) => {
          const mapState = mappings[room.room_type_id] || {};
          const isDirty = dirtyRoomIds.has(room.room_type_id);
          const isMapped = Boolean(mapState.siteminder_room_rate_uuid);
          const isSaving = savingRoomId === room.room_type_id;

          // Filter rates for current room type (or show all if no type selected)
          const availableRates = mapState.siteminder_room_type_uuid
            ? siteminderRates.filter((r) => r.roomTypeUuid === mapState.siteminder_room_type_uuid)
            : siteminderRates;

          return (
            <div
              key={room.room_type_id}
              className={`bg-white rounded-xl shadow-soft border transition-all p-5 ${
                isDirty
                  ? 'border-amber-300 ring-1 ring-amber-200'
                  : isMapped
                  ? 'border-emerald-100 hover:border-emerald-300'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
                {/* 1. Local Room Info (cols: 4) */}
                <div className="lg:col-span-4 flex items-start gap-3">
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border">
                    <img
                      src={room.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=200'}
                      alt={room.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 left-0 bg-black/60 text-white text-[9px] font-bold px-1 rounded-br">
                      #{rooms.indexOf(room) + 1}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm truncate" title={room.name}>
                      {room.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{room.description}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600">
                      <span>Harga Dasar: <strong>Rp {room.base_price?.toLocaleString('id-ID')}</strong></span>
                      <span>•</span>
                      <span>Max {room.max_guests} Tamu</span>
                    </div>
                  </div>
                </div>

                {/* 2. SiteMinder Room Type Select (cols: 3) */}
                <div className="lg:col-span-3">
                  <Label className="text-xs font-medium text-gray-700 mb-1 block">
                    1. Tipe Kamar SiteMinder
                  </Label>
                  <select
                    value={mapState.siteminder_room_type_uuid || ''}
                    onChange={(e) => handleTypeChange(room.room_type_id, e.target.value)}
                    className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 py-1 text-xs text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={!statusInfo.connected}
                  >
                    <option value="">-- Pilih Tipe Kamar SiteMinder --</option>
                    {siteminderTypes.map((t) => (
                      <option key={t.uuid} value={t.uuid}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. SiteMinder Rate Plan Select (cols: 3) */}
                <div className="lg:col-span-3">
                  <Label className="text-xs font-medium text-gray-700 mb-1 block">
                    2. Rate Plan SiteMinder (Data Harga/Allotment)
                  </Label>
                  <select
                    value={mapState.siteminder_room_rate_uuid || ''}
                    onChange={(e) => handleRateChange(room.room_type_id, e.target.value)}
                    className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 py-1 text-xs text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={!statusInfo.connected}
                  >
                    <option value="">-- Pilih Rate Plan --</option>
                    {availableRates.map((r) => (
                      <option key={r.uuid} value={r.uuid}>
                        {r.name} {r.rate ? `(Rp ${Math.round(r.rate).toLocaleString('id-ID')})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. Action & Status (cols: 2) */}
                <div className="lg:col-span-2 flex flex-col sm:flex-row lg:flex-col items-end sm:items-center lg:items-end justify-center gap-2">
                  <div className="flex items-center gap-1.5">
                    {isMapped ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                        <Check className="w-3 h-3 text-emerald-600" />
                        Terhubung
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                        Belum Terhubung
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {isMapped && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openTestModal(room)}
                        className="h-8 px-2 text-xs text-emerald-700 hover:bg-emerald-50"
                        title="Tes live quote ketersediaan & harga kamar dari SiteMinder"
                      >
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        Tes Quote
                      </Button>
                    )}

                    {isMapped && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUnmapRoom(room.room_type_id)}
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                        title="Lepas koneksi ke SiteMinder"
                      >
                        <Unlink className="w-3.5 h-3.5" />
                      </Button>
                    )}

                    <Button
                      size="sm"
                      onClick={() => handleSaveSingleRoom(room.room_type_id)}
                      disabled={isSaving || !isDirty}
                      className={`h-8 px-2.5 text-xs ${
                        isDirty
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-400 border border-gray-200'
                      }`}
                      title={isDirty ? 'Simpan perubahan pemetaan kamar ini' : 'Tidak ada perubahan'}
                    >
                      <Save className={`w-3.5 h-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Connected Details Bar if Mapped */}
              {isMapped && (
                <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-wrap items-center justify-between text-xs text-gray-500 bg-gray-50/70 -mx-5 -mb-5 px-5 py-2 rounded-b-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Tersambung ke:</span>
                    <span className="font-semibold text-emerald-800">
                      {mapState.siteminder_room_type_name || 'Tipe Kamar'}
                    </span>
                    <span className="text-gray-300">→</span>
                    <span className="font-semibold text-teal-800">
                      {mapState.siteminder_room_rate_name || 'Rate Plan'}
                    </span>
                    {mapState.is_manual_mapping && (
                      <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded text-[10px] font-medium">
                        Manual Choice
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 font-mono">
                    Rate UUID: {mapState.siteminder_room_rate_uuid}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredRooms.length === 0 && (
          <div className="bg-white rounded-xl shadow-soft p-12 text-center">
            <p className="text-gray-500 text-sm">Tidak ada kamar yang sesuai dengan filter pencarian.</p>
          </div>
        )}
      </div>

      {/* Test Quote Modal */}
      <Dialog open={testModal.open} onOpenChange={(open) => setTestModal((prev) => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Tes Live Quote SiteMinder
            </DialogTitle>
            <DialogDescription>
              Kamar Web: <strong>{testModal.room?.name}</strong>
              <br />
              Rate SiteMinder:{' '}
              <span className="text-emerald-700 font-medium">
                {mappings[testModal.room?.room_type_id]?.siteminder_room_rate_name || '-'}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tanggal Check-In</Label>
                <Input
                  type="date"
                  value={testModal.checkIn}
                  onChange={(e) => setTestModal((prev) => ({ ...prev, checkIn: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">Tanggal Check-Out</Label>
                <Input
                  type="date"
                  value={testModal.checkOut}
                  onChange={(e) => setTestModal((prev) => ({ ...prev, checkOut: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Jumlah Tamu Dewasa</Label>
              <Input
                type="number"
                min="1"
                max="6"
                value={testModal.adults}
                onChange={(e) => setTestModal((prev) => ({ ...prev, adults: e.target.value }))}
                className="h-9 text-xs"
              />
            </div>

            <Button
              onClick={runTestQuote}
              disabled={testModal.loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            >
              {testModal.loading ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />
                  Menghubungi SiteMinder...
                </>
              ) : (
                <>
                  <Search className="w-3.5 h-3.5 mr-2" />
                  Periksa Ketersediaan & Harga
                </>
              )}
            </Button>

            {/* Result */}
            {testModal.result && (
              <div
                className={`p-4 rounded-xl border ${
                  testModal.result.available
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                {testModal.result.available ? (
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-1.5 text-emerald-800 font-bold">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Kamar Tersedia di SiteMinder!
                    </div>
                    <div className="flex justify-between py-1 border-b border-emerald-100">
                      <span className="text-gray-600">Sisa Kuota Allotment:</span>
                      <span className="font-bold text-gray-900">{testModal.result.availability} kamar</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-emerald-100">
                      <span className="text-gray-600">Total Harga Gross:</span>
                      <span className="font-bold text-emerald-700">
                        Rp {Math.round(testModal.result.total_price || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 pt-1">
                      Rate Plan: {testModal.result.rate_name}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-800">
                    <div className="flex items-center gap-1.5 font-bold mb-1">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Kamar Tidak Tersedia
                    </div>
                    <p>{testModal.result.message || 'Tidak ada kuota pada tanggal tersebut.'}</p>
                  </div>
                )}
              </div>
            )}

            {testModal.error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {testModal.error}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

